import { PolyPizzaSearchParams } from '@/modules/polyPizzaService/types';
import { captureError } from '@/utils/sentry';
import { runtimeEnv } from '@/lib/runtimeEnv';
import { getExternalBlob, getExternalJson } from '@/infrastructure/clients/externalContentApi';

export interface PolyPizzaHttpClient {
  get<T>(url: string, params?: Record<string, unknown>): Promise<T>;
  getBlob(url: string): Promise<Blob>;
}

export class PolyPizzaAdapter implements PolyPizzaHttpClient {
  private readonly apiBaseUrl: string;

  constructor(apiBaseUrl?: string) {
    // Poly Pizza API base URL
    this.apiBaseUrl = apiBaseUrl || (runtimeEnv.VITE_POLY_PIZZA_API_URL || process.env.VITE_POLY_PIZZA_API_URL || 'https://api.poly.pizza/v1.1');
    // The API key is added server-side by getExternalContent
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    try {
      const queryParams = new URLSearchParams();
      if (params) {
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
          }
        });
      }

      const fullUrl = `${this.apiBaseUrl}${url}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const { data, error } = await getExternalJson<T>(fullUrl);
      if (error) {
        const httpError = new Error(`HTTP ${error.status}: ${error.title}`);
        captureError(httpError, { domain: 'poly-pizza-api', operation: 'get', url, status: error.status });
        throw httpError;
      }

      return data;
    } catch (error) {
      captureError(error as Error, { domain: 'poly-pizza-api', operation: 'get', url });
      throw error;
    }
  }

  async getBlob(url: string): Promise<Blob> {
    try {
      const isExternalUrl = url.startsWith('http');
      const fullUrl = isExternalUrl ? url : `${this.apiBaseUrl}${url}`;
      const { data, error } = await getExternalBlob(fullUrl);
      if (error) {
        const httpError = new Error(`HTTP ${error.status}: ${error.title}`);
        captureError(httpError, { domain: 'poly-pizza-api', operation: 'getBlob', url, status: error.status });
        throw httpError;
      }

      return data;
    } catch (error) {
      captureError(error as Error, { domain: 'poly-pizza-api', operation: 'getBlob', url });
      throw error;
    }
  }

  // Build the search URL path and query params
  // Poly Pizza API: /search/{keyword} with keyword as PATH param
  buildSearchUrl(params: PolyPizzaSearchParams): { path: string; query: Record<string, unknown> } {
    const query: Record<string, unknown> = {};
    
    // Build path - keyword goes in URL path, not query string
    let path = '/search';
    const hasKeyword = params.query && params.query.trim();
    
    if (hasKeyword) {
      // URL encode the keyword for path
      path = `/search/${encodeURIComponent(params.query!.trim())}`;
    }
    
    // Query params are only for filters
    // Limit: default 32, max 32
    if (params.limit) query.Limit = Math.min(params.limit, 32);
    
    // Page: indexed at 0
    if (params.page !== undefined) query.Page = Math.max(0, params.page - 1); // Convert 1-based to 0-based
    
    // Category: integer 0-11
    // License: 0=CC-BY, 1=CC0
    if (params.animated) query.Animated = 1;
    if (params.license !== undefined) query.License = params.license;

    // /search without keyword requires at least one filter (Category, License, or Animated)
    // Add License=1 (CC0) as default filter when no keyword and no other filters
    if (!hasKeyword && !query.Animated && query.License === undefined) {
      query.License = 1; // CC0 models
    }

    return { path, query };
  }
}
